import * as React from "react";
import {
    Box,
    Stack,
    Tooltip,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    TablePagination,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";

export default function KaptiveTableMUI({ data = {} }) {
    // data esperado:
    // {
    //   "Best match locus": { k: "...", oc: "..." },
    //   "Best match type": { k: "...", oc: "..." },
    //   "Match confidence": { k: "...", oc: "..." }
    // }

    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    const handleChangePage = (_e, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const rows = React.useMemo(() => {
        const locus = data?.["Best match locus"] ?? {};
        const type = data?.["Best match type"] ?? {};
        const conf = data?.["Match confidence"] ?? {};

        return [
            {
                id: "kaptive_k",
                kaptive: "K",
                best_match_locus: locus.k ?? "—",
                best_match_type: type.k ?? "—",
                match_confidence: conf.k ?? "—",
            },
            {
                id: "kaptive_oc",
                kaptive: "O",
                best_match_locus: locus.oc ?? "—",
                best_match_type: type.oc ?? "—",
                match_confidence: conf.oc ?? "—",
            },
        ];
    }, [data]);

    const paginatedRows = rows.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    // CSV “bonito” (ordenado / renombrado)
    const csvData = React.useMemo(
        () => ({
            title: "kaptive",
            columns: [
                { key: "kaptive", label: "Kaptive" },
                { key: "best_match_locus", label: "Best match locus" },
                { key: "best_match_type", label: "Best match type" },
                { key: "match_confidence", label: "Match confidence" },
            ],
            rows,
        }),
        [rows]
    );

    // ======== ALTURA DINÁMICA SEGÚN FILAS VISIBLES ========
    // Ajusta estos números si tu theme cambia tamaños
    const ROW_H = 53; // alto de cada fila
    const HEAD_H = 56; // alto del header
    const PAGINATION_H = 52; // alto de la paginación

    const visibleCount = paginatedRows.length;
    const paperHeight = HEAD_H + visibleCount * ROW_H + PAGINATION_H;

    return (
        <Box sx={{ width: "100%" }}>
            <ToolbarCSV data={csvData} />

            <Paper
                elevation={2}
                sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    height: paperHeight, // ✅ se ajusta a filas
                }}
            >
                <TableContainer
                    sx={{
                        flex: 1,
                        overflow: "hidden", // ✅ sin scroll interno
                    }}
                >
                    <Table
                        stickyHeader
                        aria-label="kaptive summary"
                        sx={{
                            width: "100%",
                            tableLayout: "fixed",
                            "& th": {
                                bgcolor: "#000",
                                color: "#fff",
                                fontWeight: 700,
                                height: HEAD_H, // ✅ para que el cálculo sea exacto
                            },
                            "& tbody tr": {
                                height: ROW_H, // ✅ para que el cálculo sea exacto
                            },
                            "& td, & th": { borderColor: "divider" },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" sx={{ width: "16%" }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Kaptive
                                    </Typography>
                                </TableCell>

                                <TableCell align="center" sx={{ width: "28%" }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Best match locus
                                    </Typography>
                                </TableCell>

                                <TableCell align="center" sx={{ width: "28%" }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Best match type
                                    </Typography>
                                </TableCell>

                                <TableCell align="center" sx={{ width: "28%" }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Match confidence
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {paginatedRows.map((r) => (
                                <TableRow key={r.id}>
                                    <TableCell align="center">
                                        <Typography variant="body2" fontWeight={700}>
                                            {r?.kaptive ?? "—"}
                                        </Typography>
                                    </TableCell>

                                    <TableCell align="center">
                                        <Typography variant="body2">
                                            {r?.best_match_locus ?? "—"}
                                        </Typography>
                                    </TableCell>

                                    <TableCell align="center">
                                        <Typography variant="body2">
                                            {r?.best_match_type ?? "—"}
                                        </Typography>
                                    </TableCell>

                                    <TableCell align="center">
                                        <Typography variant="body2">
                                            {r?.match_confidence ?? "—"}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    sx={{ mt: "auto" }}
                    count={rows.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[2, 5, 10]}
                />
            </Paper>
        </Box>
    );
}

/* ===================== ToolbarCSV (igual patrón) ===================== */

// Escape RFC4180-ish: comillas dobles, comas, saltos de linea
const escapeCSV = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    const mustQuote = /[",\n\r]/.test(str);
    const escaped = str.replace(/"/g, '""');
    return mustQuote ? `"${escaped}"` : escaped;
};

const normalizeCell = (v) => {
    if (Array.isArray(v)) return v.join(", ");
    return v;
};

const objectToCSV = ({ columns = [], rows = [] }) => {
    const headers = columns.map((c) => escapeCSV(c.label ?? c.key ?? ""));
    const lines = [headers.join(",")];

    for (const r of rows) {
        const row = columns.map((c) => {
            const key = c.key;
            const value = key ? normalizeCell(r?.[key]) : "";
            return escapeCSV(value);
        });
        lines.push(row.join(","));
    }

    return lines.join("\r\n");
};

const rowsArrayToCSV = (rows = []) => {
    const keys = rows[0] ? Object.keys(rows[0]) : [];
    const headers = keys.map(escapeCSV);
    const lines = [headers.join(",")];

    for (const r of rows) {
        const line = keys.map((k) => escapeCSV(normalizeCell(r?.[k])));
        lines.push(line.join(","));
    }

    return lines.join("\r\n");
};

const safeFilename = (name) =>
    String(name || "kaptive")
        .trim()
        .replace(/[^\w\-]+/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "")
        .toLowerCase();

function ToolbarCSV({ data, rows }) {
    const csv = React.useMemo(() => {
        if (data?.columns && data?.rows) return objectToCSV(data);
        return rowsArrayToCSV(rows ?? []);
    }, [data, rows]);

    const filename = React.useMemo(() => {
        const base = safeFilename(data?.title) || "kaptive";
        return `${base}.csv`;
    }, [data?.title]);

    const copyCSV = async () => {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(csv);
                return;
            }
        } catch {
            // fallback
        }

        const ta = document.createElement("textarea");
        ta.value = csv;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
    };

    const downloadCSV = () => {
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <Tooltip title="Copiar CSV">
                <IconButton size="small" onClick={copyCSV}>
                    <ContentCopyIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Descargar CSV">
                <IconButton size="small" onClick={downloadCSV}>
                    <DownloadIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Stack>
    );
}